/**
 * Input Scope picker — which items feed Planning Engine views and runs.
 * Compact control in the engine chrome (SearchableSelect for named sets).
 *
 * Q / sort / tag query UX lives in ScopeQueryControls.tsx for the future
 * first-class scope-query type — not mounted here.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronDown,
  Database,
  FileCheck,
  FolderOpen,
  Inbox,
  Layers,
  Loader2,
} from 'lucide-react';
import { SearchableSelect } from '../../../components/shared/SearchableSelect';
import {
  listCollectionsForPlanning,
  listItemPools,
  listPublishedPriorityLists,
  listQueuesForPlanning,
  type PlanningInputOption,
} from '../../../lib/planningService';
import {
  engineInputKindLabel,
  type EngineInputSource,
} from '../../../lib/planningEngineService';

const SCENARIO_PORTFOLIO_TILE = {
  value: 'scenario_portfolio' as const,
  label: 'Scenario priorities',
  description: 'Only priorities already on this scenario (hard container — not an Item Pool)',
  icon: Layers,
  color: {
    bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30',
    text: 'text-[var(--text-sec)]',
    border: 'border-[var(--slate)] dark:border-[var(--slate)]',
    iconColor: 'text-[var(--text-ter)]',
  },
};

const SOURCE_TYPE_TILES = [
  {
    value: 'live' as const,
    label: 'All Live Items',
    description: 'Every active item in the workspace',
    icon: Database,
    color: {
      bg: 'bg-[var(--surface)] dark:bg-[var(--bg)]',
      text: 'text-[var(--text-sec)] dark:text-[var(--text-pri)]',
      border: 'border-[var(--rule)] dark:border-[var(--rule-bright)]',
      iconColor: 'text-[var(--text-ter)]',
    },
  },
  {
    value: 'queue' as const,
    label: 'Queue',
    description: 'Items routed to an org-unit intake queue',
    icon: Inbox,
    color: {
      bg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30',
      text: 'text-[var(--mustard)] dark:text-[var(--mustard)]',
      border: 'border-[var(--mustard)] dark:border-[var(--mustard)]',
      iconColor: 'text-[var(--mustard)]',
    },
  },
  {
    value: 'item_pool' as const,
    label: 'Item Pool',
    description: 'A locked-capable subset for ranking / planning',
    icon: Layers,
    color: {
      bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30',
      text: 'text-[var(--text-sec)]',
      border: 'border-[var(--slate)] dark:border-[var(--slate)]',
      iconColor: 'text-[var(--text-ter)]',
    },
  },
  {
    value: 'collection' as const,
    label: 'Collection',
    description: 'A lightweight curated grouping',
    icon: FolderOpen,
    color: {
      bg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30',
      text: 'text-[var(--text-sec)]',
      border: 'border-[var(--slate)] dark:border-[var(--slate)]',
      iconColor: 'text-[var(--text-ter)]',
    },
  },
  {
    value: 'published_priority_list' as const,
    label: 'Published List',
    description: 'Authoritative ranked priorities',
    icon: FileCheck,
    color: {
      bg: 'bg-[var(--green-dim)] dark:bg-[var(--green)]/30',
      text: 'text-[var(--green)] dark:text-[var(--text-ter)]',
      border: 'border-[var(--green)] dark:border-[var(--green)]',
      iconColor: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    },
  },
] as const;

type SourceTypeValue =
  | (typeof SOURCE_TYPE_TILES)[number]['value']
  | typeof SCENARIO_PORTFOLIO_TILE.value;

function selectPrompt(type: SourceTypeValue): string {
  switch (type) {
    case 'queue':
      return 'Select a Queue';
    case 'item_pool':
      return 'Select an Item Pool';
    case 'collection':
      return 'Select a Collection';
    case 'published_priority_list':
      return 'Select a Published List';
    case 'scenario_portfolio':
      return 'Scenario priorities (fixed to this scenario)';
    default:
      return 'Select…';
  }
}

export function EngineInputScopePicker({
  current,
  onChange,
  variant = 'card',
  /** Slice 0: when true, All Live Items tile is disabled (hard-scoped scenario). */
  lockLive = false,
}: {
  current: EngineInputSource;
  onChange: (src: EngineInputSource) => void;
  variant?: 'card' | 'compact' | 'tiles';
  lockLive?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<SourceTypeValue>(() =>
    current.type === 'scenario_portfolio'
      ? 'scenario_portfolio'
      : SOURCE_TYPE_TILES.some((t) => t.value === current.type)
        ? (current.type as SourceTypeValue)
        : 'item_pool',
  );
  const [selectedId, setSelectedId] = useState('name' in current ? current.id : '');
  const [options, setOptions] = useState<PlanningInputOption[]>([]);
  const [loadingOpts, setLoadingOpts] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Keep local type in sync when parent restores bound scope (e.g. scenario activate).
  useEffect(() => {
    if (current.type === 'scenario_portfolio') {
      setSelectedType('scenario_portfolio');
      setSelectedId('id' in current ? current.id : '');
    } else if (SOURCE_TYPE_TILES.some((t) => t.value === current.type)) {
      setSelectedType(current.type as SourceTypeValue);
      setSelectedId('id' in current ? current.id : '');
    }
  }, [current]);

  useEffect(() => {
    const shouldLoad =
      variant === 'tiles'
        ? selectedType !== 'live'
        : open && selectedType !== 'live';
    if (!shouldLoad) {
      setOptions([]);
      return;
    }
    setLoadingOpts(true);
    setSelectedId(current.type === selectedType && 'id' in current ? current.id : '');

    // Scenario priorities are not a pick-list of pools — they are this scenario's rows.
    if (selectedType === 'scenario_portfolio') {
      if (current.type === 'scenario_portfolio' && 'id' in current) {
        setOptions([
          {
            id: current.id,
            name: current.name || 'This scenario’s priorities',
            itemCount: undefined,
          },
        ]);
        setSelectedId(current.id);
      } else {
        setOptions([]);
      }
      setLoadingOpts(false);
      return;
    }

    const loaders: Record<string, () => Promise<{ data: PlanningInputOption[] }>> = {
      queue: listQueuesForPlanning,
      item_pool: listItemPools,
      collection: listCollectionsForPlanning,
      published_priority_list: listPublishedPriorityLists,
    };
    void loaders[selectedType]().then(({ data }) => {
      setOptions(data);
      setLoadingOpts(false);
    });
  }, [selectedType, open, current, variant]);

  const openPopover = useCallback(() => {
    setSelectedType(
      current.type === 'scenario_portfolio'
        ? 'scenario_portfolio'
        : SOURCE_TYPE_TILES.some((t) => t.value === current.type)
          ? (current.type as SourceTypeValue)
          : 'item_pool',
    );
    setSelectedId('name' in current ? current.id : '');
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const popoverH = 360;
      const openUpward = spaceBelow < popoverH && rect.top > popoverH;
      setPopoverStyle({
        position: 'fixed',
        left: Math.min(rect.left, window.innerWidth - 360),
        width: Math.max(rect.width, 340),
        zIndex: 9999,
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + 6 }
          : { top: rect.bottom + 6 }),
      });
    }
    setOpen(true);
  }, [current]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!document.contains(t)) return;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(t) &&
        popoverRef.current &&
        !popoverRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [open]);

  // Position is computed once at open time (fixed to viewport coords) and
  // never recalculated — on scroll the trigger moves with the page while the
  // popover stays put, visually detaching. Close instead of tracking scroll.
  //
  // Capture-phase, because a scroll event does not bubble: this is the only way
  // to see the page scroll under a nested container. The cost of capture is
  // that scrolls originating INSIDE the popover arrive here too — and the
  // popover has its own overflow-y-auto list. Scrolling that list does not move
  // the trigger, so the popover is still anchored correctly and must stay open.
  // Without this guard the control is unusable the moment the list overflows,
  // which is exactly when a list needs scrolling (defect: 2026-09-02 demo).
  useEffect(() => {
    if (!open) return;
    const handler = (e: Event) => {
      const target = e.target as Node | null;
      if (target && popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', handler, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', handler, { capture: true });
  }, [open]);

  const applySource = useCallback(
    (src: EngineInputSource) => {
      onChange(src);
      setOpen(false);
    },
    [onChange],
  );

  const currentTile =
    current.type === 'scenario_portfolio'
      ? SCENARIO_PORTFOLIO_TILE
      : (SOURCE_TYPE_TILES.find((t) => t.value === current.type) ?? SOURCE_TYPE_TILES[0]);
  const TriggerIcon = currentTile.icon;
  const label =
    current.type === 'live'
      ? 'All Live Items'
      : current.type === 'scenario_portfolio'
        ? current.name || 'This scenario’s priorities'
        : 'name' in current
          ? current.name
          : '';

  const pickerTiles =
    current.type === 'scenario_portfolio' || selectedType === 'scenario_portfolio'
      ? [SCENARIO_PORTFOLIO_TILE, ...SOURCE_TYPE_TILES]
      : [...SOURCE_TYPE_TILES];

  const orphanedNamedScope =
    current.type !== 'live' &&
    'id' in current &&
    selectedType === current.type &&
    selectedType !== 'scenario_portfolio' &&
    !loadingOpts &&
    options.length > 0 &&
    !options.some((o) => o.id === current.id);

  /**
   * F10: the picker used to render `options` in fetch order behind a
   * SearchableSelect whose closed trigger shows only the current selection —
   * so a list published moments ago read as "the new list isn't there" and cost
   * minutes in the demo. Two fixes, one shared by both picker variants:
   * newest-decided first (with the newest badged), and the availability count
   * visible without opening the control.
   */
  const orderedOptions = useMemo(
    () =>
      [...options].sort((a, b) => {
        const at = a.decidedAt ? Date.parse(a.decidedAt) : 0;
        const bt = b.decidedAt ? Date.parse(b.decidedAt) : 0;
        return bt - at;
      }),
    [options],
  );

  const newestOptionId = orderedOptions[0]?.decidedAt ? orderedOptions[0].id : null;

  const selectOptions = useMemo(
    () =>
      orderedOptions.map((o) => ({
        value: o.id,
        label: [
          o.name,
          o.itemCount ? `(${o.itemCount})` : '',
          // A ratified list is plannable but not yet the Plan of Record (F15).
          o.state === 'ratified' ? '· Ratified' : '',
          o.id === newestOptionId ? '· newest' : '',
        ]
          .filter(Boolean)
          .join(' '),
      })),
    [orderedOptions, newestOptionId],
  );

  const kindLabel = engineInputKindLabel(selectedType).toLowerCase();

  const scopeSelect = (
    <>
      <SearchableSelect
        options={selectOptions}
        value={selectedId}
        onChange={(id) => {
          const opt = options.find((o) => o.id === id);
          if (!opt) return;
          applySource({
            type: selectedType,
            id: opt.id,
            name: opt.name,
          } as EngineInputSource);
        }}
        placeholder={`Select a ${kindLabel} (${selectOptions.length} available)`}
      />
      <p className="mt-1.5 text-[11px] text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        {selectOptions.length} {kindLabel}
        {selectOptions.length === 1 ? '' : 's'} available · newest first
      </p>
    </>
  );

  const popover = open ? (
    <div
      ref={popoverRef}
      style={popoverStyle}
      className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] shadow-2xl overflow-hidden"
    >
      <div className="px-4 pt-4 pb-3 border-b border-[var(--rule)] dark:border-[var(--rule)]">
        <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">
          Input Scope
        </p>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">
          Which items appear in Priorities, Achievability, and Sync &amp; Run
        </p>
      </div>

      <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
        {orphanedNamedScope && (
          <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)] bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40 border border-[var(--mustard)] dark:border-[var(--mustard)] px-3 py-2">
            Current scope “{label}” is not in this list (deleted or other workspace). Pick a valid
            scope — do not leave this selected or lists may look empty or wrong.
          </p>
        )}
        {selectedType === 'scenario_portfolio' && (
          <p className="text-xs text-[var(--text-sec)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 border border-[var(--slate)] dark:border-[var(--slate)] px-3 py-2">
            <strong>Scenario priorities</strong> means only rows already on this scenario — not an
            Item Pool or investment portfolio. Prefer binding an Item Pool
            when you want a named selectable set.
          </p>
        )}
        <div className="grid grid-cols-2 gap-2">
          {pickerTiles.map((tile) => {
            const isSelected = selectedType === tile.value;
            const Icon = tile.icon;
            const liveLocked = lockLive && tile.value === 'live';
            return (
              <button
                key={tile.value}
                type="button"
                disabled={liveLocked}
                title={
                  liveLocked
                    ? 'Hard-scoped scenario — All Live Items is locked'
                    : undefined
                }
                onClick={() => {
                  if (tile.value === 'live') {
                    if (lockLive) return;
                    applySource({ type: 'live' });
                  } else {
                    setSelectedType(tile.value);
                  }
                }}
                className={`flex items-start gap-3 p-3  border text-left transition-all ${
                  liveLocked
                    ? 'opacity-40 cursor-not-allowed bg-[var(--surface)] dark:bg-[var(--bg)] border-[var(--rule)]'
                    : isSelected
                      ? `${tile.color.bg} ${tile.color.border} ring-1 ${tile.color.border.replace('border-', 'ring-')}`
                      : 'bg-[var(--bg-raised)] dark:bg-[var(--bg)] border-[var(--rule)] dark:border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <Icon
                  className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isSelected ? tile.color.iconColor : 'text-[var(--text-ter)]'}`}
                />
                <div className="min-w-0">
                  <div
                    className={`text-sm font-medium ${isSelected ? tile.color.text : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'}`}
                  >
                    {tile.label}
                    {liveLocked ? ' (locked)' : ''}
                  </div>
                  <div className={`text-xs mt-0.5 ${isSelected ? tile.color.text : 'text-[var(--text-ter)]'}`}>
                    {liveLocked
                      ? 'Scenario hard scope — cannot escape to workspace live items'
                      : tile.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {selectedType !== 'live' && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)]">
              {selectPrompt(selectedType)}
            </p>
            {loadingOpts ? (
              <div className="flex items-center gap-2 text-xs text-[var(--text-ter)] py-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading…
              </div>
            ) : options.length === 0 ? (
              <p className="text-xs text-[var(--text-ter)] italic py-2">
                {selectedType === 'queue'
                  ? 'No queues enabled. Enable an org unit as a queue in Settings → Queues.'
                  : selectedType === 'scenario_portfolio'
                    ? 'No scenario-priority scope is active. Pick an Item Pool or Collection below.'
                    : 'None found in this workspace.'}
              </p>
            ) : selectedType === 'scenario_portfolio' ? (
              <button
                type="button"
                onClick={() => {
                  const opt = options[0];
                  if (!opt) return;
                  applySource({
                    type: 'scenario_portfolio',
                    id: opt.id,
                    name: opt.name,
                  });
                }}
                className="w-full text-left border border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 px-3 py-2 text-sm text-[var(--text-sec)]"
              >
                Keep: {options[0]?.name}
              </button>
            ) : (
              <div>{scopeSelect}</div>
            )}
          </div>
        )}
      </div>
    </div>
  ) : null;

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      onClick={openPopover}
      className={`flex items-center gap-2.5  border text-left transition-all hover:border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-indigo-200 ${
        variant === 'compact' ? 'px-2.5 py-1.5 max-w-md' : 'w-full px-3 py-2.5'
      } ${
        open
          ? 'border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 ring-1 ring-indigo-200'
          : 'border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)]'
      }`}
    >
      <TriggerIcon className={`h-4 w-4 flex-shrink-0 ${currentTile.color.iconColor}`} />
      <span className="flex-1 min-w-0 text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
        {current.type === 'live' ? (
          'All Live Items'
        ) : (
          <>
            <span className="text-[var(--text-ter)] font-normal">{currentTile.label}: </span>
            {label}
          </>
        )}
      </span>
      <ChevronDown
        className={`h-3.5 w-3.5 flex-shrink-0 text-[var(--text-ter)] transition-transform ${open ? 'rotate-180' : ''}`}
      />
    </button>
  );

  const tilePicker = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-2">
        {SOURCE_TYPE_TILES.map((tile) => {
          const isSelected = selectedType === tile.value;
          const Icon = tile.icon;
          return (
            <button
              key={tile.value}
              type="button"
              onClick={() => {
                if (tile.value === 'live') {
                  applySource({ type: 'live' });
                } else {
                  setSelectedType(tile.value);
                }
              }}
              className={`flex items-start gap-3 p-4  border text-left transition-all min-h-[44px] ${
                isSelected
                  ? `${tile.color.bg} ${tile.color.border} ring-1 ${tile.color.border.replace('border-', 'ring-')}`
                  : 'bg-[var(--bg-raised)] dark:bg-[var(--bg)] border-[var(--rule)] dark:border-[var(--rule)]'
              }`}
            >
              <Icon
                className={`h-5 w-5 mt-0.5 flex-shrink-0 ${isSelected ? tile.color.iconColor : 'text-[var(--text-ter)]'}`}
              />
              <div className="min-w-0">
                <div
                  className={`text-sm font-medium ${isSelected ? tile.color.text : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'}`}
                >
                  {tile.label}
                </div>
                <div className={`text-xs mt-0.5 ${isSelected ? tile.color.text : 'text-[var(--text-ter)]'}`}>
                  {tile.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {selectedType !== 'live' && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)]">
            {selectPrompt(selectedType)}
          </p>
          {loadingOpts ? (
            <div className="flex items-center gap-2 text-xs text-[var(--text-ter)] py-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading…
            </div>
          ) : options.length === 0 ? (
            <p className="text-xs text-[var(--text-ter)] italic py-2">
              {selectedType === 'queue'
                ? 'No queues enabled. Enable an org unit as a queue in Settings → Queues.'
                : 'None found in this workspace.'}
            </p>
          ) : (
            <div>{scopeSelect}</div>
          )}
        </div>
      )}
    </div>
  );

  if (variant === 'tiles') {
    return tilePicker;
  }

  if (variant === 'compact') {
    return (
      <>
        {trigger}
        {createPortal(popover, document.body)}
      </>
    );
  }

  return (
    <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-6">
      <h2 className="text-sm font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider mb-3">
        Input Scope
      </h2>
      {trigger}
      {createPortal(popover, document.body)}
    </div>
  );
}
